/**
 * API Configuration for Question Review System Frontend
 * Manages endpoints for the separated FastAPI servers
 */

const API_CONFIG = {
  // Reviewer Assigner unified server (Similarity + Assignment on same service)
  REVIEWER: {
    BASE_URL: "/reviewer",
    ENDPOINTS: {
      // Similarity subpaths
      GENERATE: "/similarity/generate",
      GENERATE_CSV: "/similarity/generate-from-csv",
      CSV_TEMPLATES: "/similarity/csv-templates",
      DEMO: "/similarity/demo",
      HEALTH: "/similarity/health",
      STATUS: "/similarity/api/status",
      // Assignment subpaths
      RUN: "/assignment/run",
      JOB: "/assignment/job",
      JOBS: "/assignment/jobs",
      SAVE: "/assignment/save",
      // new subpath
      RUN_EXPERIENCE: "/assignment/run-experience",
    },
  },

  // Upload Server (Port 8016)
  UPLOAD: {
    BASE_URL: "/upload",
    ENDPOINTS: {
      UPLOAD: '/upload-question-papers',
      COURSE_DATA: '/course-data',
      QUESTION_PAPERS: '/question-papers',
      QUESTION_PAPER: '/question-papers',
      QUESTION_DOWNLOAD: '/questions',
      CREATE_PDF_FROM_IMAGES: '/create-pdf-from-images',
      CROP_QUESTION: '/crop-question',
      DEBUG_COORDINATES: '/debug-coordinates',
      DELETE_QUESTION: '/questions',
      UPDATE_QUESTION_MARKS: '/questions',
      HEALTH: '/health',
      STATUS: '/api/status'
    }
  },

  // Analysis Server (Port 8017)
  ANALYSIS: {
    BASE_URL: "/analysis",
    ENDPOINTS: {
      CONFIG: "/config",
      ANALYZE_PAPER: "/analyze-paper",
      ANALYZE_QUESTION: "/analyze-question",
      PAPER_METADATA: "/paper-metadata",
      ANALYZE_PAPER_CONCURRENT: "/analyze-paper-concurrent",
      ANALYZE_PROGRESS: "/analyze-progress",
      QUESTION_PDF: "/question-pdf",
      HEALTH: "/health",
      STATUS: "/api/status",
    },
  },

  // Database Server (Port 8018)
  DATABASE: {
    BASE_URL: "/postgres",
    ENDPOINTS: {
      QUESTION_PAPERS: "/question-papers",
      QUESTION_PAPER: "/question-papers",
      QUESTIONS: "/questions",
      QUESTION: "/questions",
      ANALYSIS_RESULTS: "/analysis-results",
      ANALYSIS_RESULT: "/analysis-results",
      STATS: "/stats",
      HEALTH: "/health",
      STATUS: "/api/status",

      // Analytics (new) ---
      ANALYTICS_OPTIONS: "/options",
      ANALYTICS_KPIS: "/kpis",
      ANALYTICS_QUALITY_TREND: "/quality-trend",
      ANALYTICS_BLOOMS_STACKED: "/blooms/stacked",
      ANALYTICS_RUBRIC_CRITERIA: "/analytics/rubric-criteria",

      // Review endpoints
      REVIEW_MAPPINGS: '/review/mappings',
      REVIEW_REVIEWERS: '/review/mappings',
      REVIEW_COURSES: '/review/mappings',
      REVIEW_PROFESSOR_EMAILS: '/review/professor-emails',
      REVIEW_ASSIGNED_COURSES: '/review/assigned-courses',
      REVIEW_ALL_COURSES: '/review/all-courses',
      REVIEW_QUESTION_PAPERS: '/review/question-papers',
      REVIEW_PAPER_REVIEW: '/review/paper-review',
      REVIEW_QUESTION_REVIEW: '/review/question-review',
      REVIEW_PAPER_FEEDBACK: '/review/paper-feedback',
      REVIEW_ALL_EXAM_TYPES: '/review/all-exam-types',
      REVIEW_ALL_EXAM_FORMATS: '/review/all-exam-formats',
      REVIEW_ALL_UPLOADERS: '/review/all-uploaders',
      // Rubric endpoints
      RUBRICS: '/rubrics',
      RUBRIC: '/rubrics',
      RUBRIC_CRITERIA: '/rubrics',
      RUBRIC_BLOOM_TAXONOMY: '/rubrics',
      RUBRIC_VALIDATE: '/rubrics',
      
      // Custom fields endpoints
      CUSTOM_FIELDS: '/api/custom-fields',
      CUSTOM_FIELD: '/api/custom-fields',
      CUSTOM_FIELDS_INITIALIZE: '/api/custom-fields/initialize-system-fields',
      
      // CSV uploads endpoints
      CSV_UPLOADS: '/csv-uploads',
      
      // Feedback endpoints
      FEEDBACK_SUBMIT: '/feedback/submit',
      FEEDBACK_ALL: '/feedback/all'
    }
  }
};

// Helper function to get full URL for an endpoint
export const getApiUrl = (server, endpoint) => {
  const serverConfig = API_CONFIG[server];
  if (!serverConfig) {
    throw new Error(`Unknown server: ${server}`);
  }

  const endpointPath = serverConfig.ENDPOINTS[endpoint];
  if (!endpointPath) {
    throw new Error(`Unknown endpoint: ${endpoint} for server: ${server}`);
  }

  return `${serverConfig.BASE_URL}${endpointPath}`;
};

// Helper function to get base URL for a server
export const getBaseUrl = (server) => {
  const serverConfig = API_CONFIG[server];
  if (!serverConfig) {
    throw new Error(`Unknown server: ${server}`);
  }
  return serverConfig.BASE_URL;
};

// Server names for easy reference
export const SERVERS = {
  REVIEWER: "REVIEWER",
  UPLOAD: "UPLOAD",
  ANALYSIS: "ANALYSIS",
  DATABASE: "DATABASE",
};

// Endpoint names for easy reference
export const ENDPOINTS = {
  // Similarity endpoints
  SIMILARITY_GENERATE: "GENERATE",
  SIMILARITY_CSV: "GENERATE_CSV",
  SIMILARITY_CSV_TEMPLATES: "CSV_TEMPLATES",
  SIMILARITY_DEMO: "DEMO",

  // Assignment endpoints
  ASSIGNMENT_RUN: "RUN",
  ASSIGNMENT_JOB: "JOB",
  ASSIGNMENT_JOBS: "JOBS",
  ASSIGNMENT_SAVE: "SAVE",
  // new assignment endpoint
  ASSIGNMENT_RUN_EXPERIENCE: "RUN_EXPERIENCE",

  // Upload endpoints
  UPLOAD_PAPERS: 'UPLOAD',
  UPLOAD_COURSE_DATA: 'COURSE_DATA',
  QUESTION_PAPERS: 'QUESTION_PAPERS',
  QUESTION_PAPER: 'QUESTION_PAPER',
  QUESTION_DOWNLOAD: 'QUESTION_DOWNLOAD',
  CREATE_PDF_FROM_IMAGES: 'CREATE_PDF_FROM_IMAGES',
  CROP_QUESTION: 'CROP_QUESTION',
  DEBUG_COORDINATES: 'DEBUG_COORDINATES',
  DELETE_QUESTION: 'DELETE_QUESTION',
  UPDATE_QUESTION_MARKS: 'UPDATE_QUESTION_MARKS',
  
  // Analysis endpoints
  CONFIG: "CONFIG",
  ANALYSIS_QUESTION_PAPERS: "QUESTION_PAPERS",
  ANALYZE_PAPER: "ANALYZE_PAPER",
  ANALYZE_QUESTION: "ANALYZE_QUESTION",
  PAPER_METADATA: "PAPER_METADATA",
  ANALYZE_PAPER_CONCURRENT: "ANALYZE_PAPER_CONCURRENT",
  ANALYZE_PROGRESS: "ANALYZE_PROGRESS",
  QUESTION_PDF: "QUESTION_PDF",

  // Database endpoints
  DATABASE_QUESTION_PAPERS: "QUESTION_PAPERS",
  DATABASE_QUESTION_PAPER: "QUESTION_PAPER",
  DATABASE_QUESTIONS: "QUESTIONS",
  DATABASE_QUESTION: "QUESTION",
  DATABASE_ANALYSIS_RESULTS: "ANALYSIS_RESULTS",
  DATABASE_ANALYSIS_RESULT: "ANALYSIS_RESULT",
  DATABASE_STATS: "STATS",

  // Review endpoints
  REVIEW_MAPPINGS: "REVIEW_MAPPINGS",
  REVIEW_REVIEWERS: "REVIEW_REVIEWERS",
  REVIEW_COURSES: "REVIEW_COURSES",
  REVIEW_PROFESSOR_EMAILS: "REVIEW_PROFESSOR_EMAILS",
  REVIEW_ASSIGNED_COURSES: "REVIEW_ASSIGNED_COURSES",
  REVIEW_ALL_COURSES: "REVIEW_ALL_COURSES",
  REVIEW_QUESTION_PAPERS: "REVIEW_QUESTION_PAPERS",
  REVIEW_PAPER_REVIEW: "REVIEW_PAPER_REVIEW",
  REVIEW_QUESTION_REVIEW: "REVIEW_QUESTION_REVIEW",
  REVIEW_PAPER_FEEDBACK: "REVIEW_PAPER_FEEDBACK",
  REVIEW_ALL_EXAM_TYPES: "REVIEW_ALL_EXAM_TYPES",
  REVIEW_ALL_EXAM_FORMATS: "REVIEW_ALL_EXAM_FORMATS",
  REVIEW_ALL_UPLOADERS: "REVIEW_ALL_UPLOADERS",

  // Database analytics (new)
  DATABASE_ANALYTICS_OPTIONS: "ANALYTICS_OPTIONS",
  DATABASE_ANALYTICS_KPIS: "ANALYTICS_KPIS",
  DATABASE_ANALYTICS_QUALITY_TREND: "ANALYTICS_QUALITY_TREND",
  DATABASE_ANALYTICS_BLOOMS_STACKED: "ANALYTICS_BLOOMS_STACKED",
  ANALYTICS_RUBRIC_CRITERIA: "ANALYTICS_RUBRIC_CRITERIA",
  
  // Rubric endpoints
  RUBRICS: 'RUBRICS',
  RUBRIC: 'RUBRIC',
  RUBRIC_CRITERIA: 'RUBRIC_CRITERIA',
  RUBRIC_BLOOM_TAXONOMY: 'RUBRIC_BLOOM_TAXONOMY',
  RUBRIC_VALIDATE: 'RUBRIC_VALIDATE',
  
  // Custom fields endpoints
  CUSTOM_FIELDS: 'CUSTOM_FIELDS',
  CUSTOM_FIELD: 'CUSTOM_FIELD',
  CUSTOM_FIELDS_INITIALIZE: 'CUSTOM_FIELDS_INITIALIZE',
  
  // CSV uploads endpoints
  CSV_UPLOADS: 'CSV_UPLOADS',
  
  // Feedback endpoints
  FEEDBACK_SUBMIT: 'FEEDBACK_SUBMIT',
  FEEDBACK_ALL: 'FEEDBACK_ALL',

  // Common endpoints
  HEALTH: "HEALTH",
  STATUS: "STATUS",
};

export default API_CONFIG;

// Optional convenience helpers for Analytics --- ( if the above doesn't work )
const qs = (obj = {}) => {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export async function getAnalyticsOptions(year) {
  const base = getApiUrl(
    SERVERS.DATABASE,
    ENDPOINTS.DATABASE_ANALYTICS_OPTIONS
  );
  const res = await fetch(base + qs({ year }));
  if (!res.ok) throw new Error("Failed to load options");
  return res.json();
}

export async function getAnalyticsKpis(params) {
  const base = getApiUrl(SERVERS.DATABASE, ENDPOINTS.DATABASE_ANALYTICS_KPIS);
  const res = await fetch(base + qs(params));
  if (!res.ok) throw new Error("Failed to load KPIs");
  return res.json();
}

export async function getQualityTrend(params) {
  const base = getApiUrl(
    SERVERS.DATABASE,
    ENDPOINTS.DATABASE_ANALYTICS_QUALITY_TREND
  );
  const res = await fetch(base + qs(params));
  if (!res.ok) throw new Error("Failed to load trend");
  return res.json();
}

export async function getBloomsStacked(params) {
  const base = getApiUrl(
    SERVERS.DATABASE,
    ENDPOINTS.DATABASE_ANALYTICS_BLOOMS_STACKED
  );
  const res = await fetch(base + qs(params));
  if (!res.ok) throw new Error("Failed to load Bloom's");
  return res.json();
}

/* ===========================
 * NEW similarity + assignment helpers (experience-based)
 * =========================== */

/**
 * Fetch CSV templates and required columns for the 3-CSV flow.
 * Returns: {
 *   professors_template, courses_template, feedback_template,
 *   professors_required_columns, courses_required_columns, feedback_required_columns, notes
 * }
 */
export async function getSimilarityCsvTemplates() {
  const url = getApiUrl(SERVERS.REVIEWER, ENDPOINTS.SIMILARITY_CSV_TEMPLATES);
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to load CSV templates");
  return res.json();
}

/**
 * Upload 3 CSVs (professors, courses, feedback) to generate similarity.
 * Expects files:
 *  - professorsFile -> CSV with headers: professor_id, professor_name, dept
 *  - coursesFile -> CSV with headers: course_id, course_name, dept
 *  - feedbackFile -> CSV with headers: year_semester, faculty_name, faculty_id, course_id, course_name, total_participants
 * Returns ExperienceSimilarityResponse:
 *  { similarity, stats, labels, feedback_signals }
 */
export async function postSimilarityCsv3({
  professorsFile,
  coursesFile,
  feedbackFile,
}) {
  if (!professorsFile || !coursesFile || !feedbackFile) {
    throw new Error(
      "All three files are required: professors, courses, feedback"
    );
  }
  const url = getApiUrl(SERVERS.REVIEWER, ENDPOINTS.SIMILARITY_CSV);

  const form = new FormData();
  form.append("professors_file", professorsFile);
  form.append("courses_file", coursesFile);
  form.append("feedback_file", feedbackFile);

  const res = await fetch(url, {
    method: "POST",
    body: form,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Failed to generate similarity (3-CSV): ${res.status} ${text}`
    );
  }
  return res.json();
}

/**
 * Run experience-based assignment using similarity result.
 * Input:
 * {
 *   similarity,          // dict: pid -> { course_name_norm: score }
 *   labels,              // ExperienceSimilarityLabels
 *   feedback_signals,    // ExperienceFeedbackSignals (needs avg_students)
 *   max_courses_per_reviewer: number
 * }
 * Returns:
 * {
 *   assignment: { course_name_norm: [professor_id...] },
 *   total_assignments,
 *   unmet_demand: { course_name_norm: missingCount },
 *   demand: { course_name_norm: reviewersNeeded },
 *   labels
 * }
 */
export async function runExperienceAssignment({
  similarity,
  labels,
  feedback_signals,
  max_courses_per_reviewer,
}) {
  const url = getApiUrl(SERVERS.REVIEWER, ENDPOINTS.ASSIGNMENT_RUN_EXPERIENCE);

  const payload = {
    similarity,
    labels,
    feedback_signals,
    max_courses_per_reviewer,
  };

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Failed to run experience-based assignment: ${res.status} ${text}`
    );
  }

  return res.json();
}
